import { DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";

import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Timeout } from "react-number-format/types/types";
import QRCode from "react-qr-code";
import { useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import CircleButton from "@/components/buttons/CircleButton.tsx";
import { IconX } from "@/components/icons";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";
import TopUpForm, { TopUpOption } from "@/components/staff/payment/modal/TopUpModal/TopUpForm.tsx";

import UserRequests from "@/api/user/user.requests";
import { USER_KEYS } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";

type PaymentType = "subscription" | "top-up";

const TopUpMethodsModal = (): JSX.Element => {
  const { t } = useTranslation();
  const { handleSubmit } = useForm();
  const [params] = useSearchParams();
  const checkStatusInterval = useRef<Timeout>();

  const { goBack, openModal } = useModalRouter();

  const [option, setOption] = useState<TopUpOption>();

  const lang = useLang();

  const paymentType: PaymentType = useMemo(() => params.get("payment_type") as PaymentType, []);

  const isTopUp = paymentType == "top-up";

  const trId = useMemo(() => params.get("tr_id") ?? "", []);

  const queryClient = useQueryClient();

  const onSubmit: SubmitHandler<FieldValues> = (data): void => {
    console.info(data);
  };

  const checkTrStatus = async () => {
    const order = await UserRequests.getOrder(+trId);

    if (order.status == "performed") {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.userDetail(lang) });
      openModal(MODAL_ROUTES.ORDER_PERFORM_SUCCESS, { order_id: order.id.toString() }, true);
    }

    if (order.status == "canceled") {
      openModal(MODAL_ROUTES.ORDER_PERFORM_ERROR, {}, true);
    }
  };

  useEffect(() => {
    checkStatusInterval.current = setInterval(checkTrStatus, 3000);

    return () => {
      clearInterval(checkStatusInterval.current);
    };
  }, []);

  return (
    <>
      <div className={"w-full max-w-[63.0625rem] space-y-10 max-lg:space-y-5 max-lg:!rounded-none"}>
        <div className={"flex items-center justify-between"}>
          <DialogTitle as={"h2"} className={"text-4xl font-semibold max-lg:text-2xl"}>
            {isTopUp ? t("Payment.top-up") : t("Payment.subscription")}
          </DialogTitle>
          <CircleButton
            icon={<IconX className={"size-6 stroke-on-primary"} />}
            buttonType={"exet"}
            onClick={goBack}
            className={"bg-surface-container-high lg:hidden"}
          />
        </div>
        <hr className={"h-px w-full border-surface-container max-md:hidden"} />
        <form onSubmit={handleSubmit(onSubmit)} className={"space-y-10 max-lg:space-y-5 max-sm:!mt-4"}>
          <div className={"grid grid-cols-2 gap-16 max-lg:grid-cols-1"}>
            <TopUpForm topUpOption={option} selectOption={setOption} />
            <div className={"flex justify-center max-lg:hidden"}>
              <div className={"max-w-[18.8rem] text-center"}>
                <h3 className="mb-6">{t("Payment.qr-code-pay")}</h3>
                <AnimatePresence mode="wait">
                  {option ? (
                    <motion.div key={option?.link} animate={{ scale: 1 }} initial={{ scale: 0 }} exit={{ scale: 0 }}>
                      <QRCode
                        style={{ height: "auto", maxWidth: "18.75rem", width: "18.75rem" }}
                        value={option?.link}
                        viewBox={`0 0 256 256`}
                        bgColor="black"
                        fgColor="white"
                      />
                    </motion.div>
                  ) : (
                    <div className="flex h-[18.75rem] w-[18.75rem] items-center justify-center">
                      <p className="text-lg font-semibold">{t("Payment.select-method")}</p>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          <hr className={"h-px w-full border-surface-container max-md:hidden"} />
          <div className={"flex items-center justify-end gap-4 max-lg:w-full"}>
            <Button color={"white"} onClick={goBack} className={"w-fit max-lg:hidden"}>
              {t("Actions.cancel")}
            </Button>
            <a href={option?.link} className="w-full" target="_blank">
              <Button className={"w-fit max-lg:w-full"}>{t("Payment.pay-via-site")}</Button>
            </a>
          </div>
        </form>
      </div>
    </>
  );
};

export default TopUpMethodsModal;
